/**
 * Utility functions for handling time formatting in work entries and breaks
 * Updated to use server time synchronization for consistency across all devices
 */

import { timeService } from '../services/timeService';

/**
 * Get current timestamp in ISO format using server Romanian time
 * Format: "2024-01-01T10:30:45Z" instead of "2024-01-01T10:30:45.123Z"
 */
export function getCurrentTimestamp(): string {
  try {
    // Use server-synchronized Romanian time
    return timeService.getCurrentTimestamp();
  } catch (error) {
    console.error('TimeUtils: Failed to get server timestamp, falling back to client time:', error);
    // Fallback to client time if server is unavailable
    const now = new Date();
    now.setMilliseconds(0);
    return now.toISOString();
  }
}

/**
 * Get current date in YYYY-MM-DD format using server Romanian time
 */
export function getCurrentDateString(): string {
  try {
    // Use server-synchronized Romanian time
    return timeService.getCurrentDateString();
  } catch (error) {
    console.error('TimeUtils: Failed to get server date, falling back to client time:', error);
    // Fallback to client time if server is unavailable
    return new Date().toISOString().split('T')[0];
  }
}

/**
 * Format a timestamp for consistent time precision
 * @param date - Date object or ISO string
 * @returns ISO string without milliseconds
 */
export function formatTimestamp(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  // Remove milliseconds by setting them to 0
  dateObj.setMilliseconds(0);
  return dateObj.toISOString();
}

/**
 * Get a timestamp with added seconds to avoid timing conflicts
 * Useful when multiple operations happen in quick succession
 * @param secondsToAdd - Number of seconds to add (default: 1)
 */
export function getTimestampWithOffset(secondsToAdd: number = 1): string {
  const now = new Date();
  now.setMilliseconds(0); // Remove milliseconds
  now.setSeconds(now.getSeconds() + secondsToAdd);
  return now.toISOString();
}

/**
 * Convert UTC time to Romanian timezone for display
 * Romania is UTC+2 (standard) or UTC+3 (daylight saving)
 * @param utcDate - UTC date/time string or Date object
 * @returns Formatted string in Romanian timezone
 */
export function toRomanianTime(utcDate: Date | string): Date {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  
  // Romania is in Eastern European Time (EET/EEST)
  // UTC+2 in winter, UTC+3 in summer (DST)
  // For simplicity, we'll add the offset manually
  // Note: This is a simplified approach. For production, use a proper timezone library
  
  // Get the UTC time
  const utcTime = date.getTime();
  
  // Check if we're in DST (last Sunday of March to last Sunday of October)
  const year = date.getUTCFullYear();
  const jan = new Date(Date.UTC(year, 0, 1));
  const jul = new Date(Date.UTC(year, 6, 1));
  const janOffset = jan.getTimezoneOffset();
  const julOffset = jul.getTimezoneOffset();
  const isDST = Math.max(janOffset, julOffset) !== date.getTimezoneOffset();
  
  // Romania offset: UTC+2 (standard) or UTC+3 (DST)
  const romaniaOffset = isDST ? 3 : 2;
  
  // Create a new date with the Romanian offset
  const romanianDate = new Date(utcTime + romaniaOffset * 60 * 60 * 1000);
  
  return romanianDate;
}

/**
 * Format time for display in Romanian timezone
 * @param utcDate - UTC date/time string or Date object
 * @returns Formatted time string (HH:mm)
 */
export function formatRomanianTime(utcDate: Date | string): string {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  
  // Use Romanian locale with timezone
  return date.toLocaleTimeString('ro-RO', {
    timeZone: 'Europe/Bucharest',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * Format date and time for display in Romanian timezone
 * @param utcDate - UTC date/time string or Date object
 * @returns Formatted date and time string
 */
export function formatRomanianDateTime(utcDate: Date | string): string {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  
  // Use Romanian locale with timezone
  return date.toLocaleString('ro-RO', {
    timeZone: 'Europe/Bucharest',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * Calculate work duration correctly
 * @param startTime - Start time (UTC)
 * @param endTime - End time (UTC) or current time if not provided
 * @param breakMinutes - Break minutes to subtract
 * @returns Duration in hours with decimal
 */
export function calculateWorkDuration(
  startTime: Date | string,
  endTime?: Date | string | null,
  breakMinutes: number = 0
): number {
  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  const end = endTime 
    ? (typeof endTime === 'string' ? new Date(endTime) : endTime)
    : new Date();
  
  const durationMs = end.getTime() - start.getTime();
  const durationMinutes = durationMs / (1000 * 60);
  const netMinutes = Math.max(0, durationMinutes - breakMinutes);
  
  return netMinutes / 60; // Return hours with decimal
}

/**
 * Format duration from hours to human-readable string
 * @param hours - Duration in hours (with decimal)
 * @returns Formatted string (e.g., "2h 30m")
 */
export function formatDuration(hours: number): string {
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}